import type { AssetType, ConversionType, ModuleTemplate } from "#gen/bundles/v1/bundle_pb.js";
import type { Component as PageComponent } from "#gen/pages/v2/page_pb.js";
import type { ConfigType } from "#lib/api/bundles.js";
import type { SuiteModulesChange } from "#lib/components/flow/editor/editorState.js";
import type { JsonObject } from "@bufbuild/protobuf";
import type { AnyAction } from "@reduxjs/toolkit";
import type { Edge, Node } from "@xyflow/svelte";

export const DRAWER_WIDTH = 880;
// The left content tray's geometry. PagePalette lays itself out from these two rather than from
// Tailwind classes, so the canvas band focusViewport reserves cannot drift from the rendered tray.
// The card width is border-box: 2x1px border + 2x12px padding + the 40px icon buttons.
export const PAGE_TRAY_GUTTER = 24;
export const PAGE_TRAY_CARD_WIDTH = 66;
export const PAGE_TRAY_WIDTH = PAGE_TRAY_GUTTER + PAGE_TRAY_CARD_WIDTH;
export const PALETTE_CARD_WIDTH = 160;
export const PALETTE_INSET = PAGE_TRAY_GUTTER + PALETTE_CARD_WIDTH;
export const ANIMATION_DURATION = 400;

export interface RevealKeys {
	primary: string[];
	context: string[];
}

export interface CanvasUpdateOptions {
	fitView?: boolean;
	animate?: boolean;
	reframeSelection?: boolean;
	reveal?: RevealKeys;
}

export enum DisplayMode {
	BUILD = "build",
	PREVIEW = "preview",
	LAYOUT = "layout",
}

export enum SelectionType {
	NODE = "node",
	EDGE = "edge",
}

export type ComposeSuiteChange = (suiteChange: SuiteModulesChange) => AnyAction;

export interface Selection {
	item:
		| {
				type: SelectionType.NODE;
				value: Node;
		  }
		| {
				type: SelectionType.EDGE;
				value: Edge;
		  };
}

export interface TypeSelection {
	id: string;
	type: AssetType;
	template?: ModuleTemplate;
	config?: ConfigType;
}

export interface ComponentSelection {
	id: string;
	type: string;
	label: string;
	typeUri: string;
	config?: JsonObject;
	style?: JsonObject;
	conversionType?: ConversionType;
	isConnectable?: boolean;
}

export interface NodeMenuAnchor {
	x: number;
	y: number;
	width: number;
	height: number;
	toggle?: HTMLElement;
}

export interface CanvasNodeGestures {
	mousedown: (e: MouseEvent) => void;
	click: (key: string, e: MouseEvent) => void;
	dblclick: (e: MouseEvent) => void;
	contextmenu: (key: string, e: MouseEvent) => void;
	openMenu: (key: string, anchor: NodeMenuAnchor) => void;
	isMenuEnabled: () => boolean;
	isMenuOpenFor: (key: string) => boolean;
}

// Which connectable page element to highlight blue.
export interface HighlightedLinkable {
	hovered?: string;
	selected?: string;
}

export interface PageV2ComponentEditState {
	pageAssetKey: string;
	title: string;
	component?: PageComponent;
	onRemoveComponent?: () => Promise<void>;
	onDuplicateComponent?: () => Promise<void>;
}

export enum PreviewMode {
	DESKTOP = "desktop",
	MOBILE = "mobile",
}

export interface PaletteItemBase {
	id: string;
	name: string;
	iconSet?: string;
	icon?: string;
	description?: string;
	helpCenterArticleID?: number;
}

export interface PaletteItem extends PaletteItemBase {
	type: AssetType;
	config?: ConfigType;
}

export interface PagePaletteItem extends PaletteItemBase {
	label?: string;
	typeUri?: string;
	config?: JsonObject;
	style?: JsonObject;
	conversionType?: ConversionType;
	isConnectable?: boolean;
}
